// src/pages/exercise/components/Character/AnimationManager.jsx
import { useEffect, useRef } from 'react';
import { useStore } from '../Stores/exerciseStore';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';

// ─────────────────────────────────────────────
// ✅ TUNE HERE: scale applied to position tracks
// idle       → active during SELECTION & EXERCISE
// transition → active during TRANSITION1 & TRANSITION2
// charIndex: 1=Moose, 2=Bunny, 3=Sloth, 4=Tiger
// ─────────────────────────────────────────────
const ANIM_SCALE = {
  idle: {
    1: 1.0, // Moose
    2: 1.0, // Bunny
    3: 1.0, // Sloth
    4: 1.0, // Tiger
  },
  transition: {
    1: 0.5,  // Moose
    2: 0.35, // Bunny
    3: 0.45, // Sloth
    4: 0.35, // Tiger
  },
};

const applyScaleToClip = (clip, scale) => {
  if (scale === 1.0) return clip;
  clip.tracks.forEach((track) => {
    if (track.name.includes('.position')) {
      for (let i = 0; i < track.values.length; i++) {
        track.values[i] *= scale;
      }
    }
  });
  return clip;
};

const IDLE_NAMES = ['moose-idle', 'bunny-idle', 'sloth-idle', 'tiger-idle'];

export function AnimationManager({ targetRef }) {
  const { phase, charIndex } = useStore();
  const phaseRef = useRef(phase);
  const charIndexRef = useRef(charIndex);

  const mixersRef = useRef([]);
  const idleActionsRef = useRef([]);
  const transitionActionsRef = useRef({ t1: null, t2: null });

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { charIndexRef.current = charIndex; }, [charIndex]);

  // ─────────────────────────────────────────────
  // 1. Load FBX + build 4 mixers + start ALL idle loops
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!targetRef.current) return;

    const loader = new FBXLoader();

    const animPaths = [
      { name: 'moose-idle',  path: '/assets/models/animation/moose-idle1.fbx' },
      { name: 'bunny-idle',  path: '/assets/models/animation/bunny-idle1.fbx' },
      { name: 'sloth-idle',  path: '/assets/models/animation/sloth-idle1.fbx' },
      { name: 'tiger-idle',  path: '/assets/models/animation/tiger-idle1.fbx' },
      { name: 't1-combined', path: '/assets/models/animation/t1-combined.fbx' },
      { name: 't2-combined', path: '/assets/models/animation/t2-combined.fbx' },
    ];

    Promise.all(
      animPaths.map(({ name, path }) =>
        new Promise((resolve, reject) => {
          loader.load(
            path,
            (fbx) => {
              const clip = fbx.animations[0];
              if (!clip) { reject(`No animation in ${path}`); return; }
              clip.name = name;
              resolve(clip);
            },
            undefined,
            (err) => reject(err)
          );
        })
      )
    ).then((loadedClips) => {
      const clipMap = {};
      loadedClips.forEach(c => { clipMap[c.name] = c; });

      const characterRoots = targetRef.current.children;
      const newMixers = [];
      const newIdleActions = [];

      characterRoots.forEach((root, i) => {
        const charIdx = i + 1;
        const idleName = IDLE_NAMES[i];
        const rawClip = clipMap[idleName];

        if (!rawClip) {
          console.warn(`[AnimationManager] No clip for ${idleName}`);
          newMixers.push(null);
          newIdleActions.push(null);
          return;
        }

        // Clone → scale → native cyclic loop (no stitching needed)
        const idleClip = THREE.AnimationClip.parse(THREE.AnimationClip.toJSON(rawClip));
        idleClip.name = idleName;
        applyScaleToClip(idleClip, ANIM_SCALE.idle[charIdx] ?? 1.0);

        const mixer = new THREE.AnimationMixer(root);
        const idleAction = mixer.clipAction(idleClip);

        idleAction.setLoop(THREE.LoopRepeat, Infinity).play();
        console.log(`[AnimationManager] ✅ Idle loop started: ${idleName} (char ${charIdx})`);

        newMixers.push(mixer);
        newIdleActions.push(idleAction);
      });

      mixersRef.current = newMixers;
      idleActionsRef.current = newIdleActions;

      const activeMixer = newMixers[charIndexRef.current - 1];
      if (activeMixer) {
        const t1Clip = THREE.AnimationClip.parse(THREE.AnimationClip.toJSON(clipMap['t1-combined']));
        const t2Clip = THREE.AnimationClip.parse(THREE.AnimationClip.toJSON(clipMap['t2-combined']));
        t1Clip.name = 't1-combined';
        t2Clip.name = 't2-combined';
        applyScaleToClip(t1Clip, ANIM_SCALE.transition[charIndexRef.current] ?? 0.5);
        applyScaleToClip(t2Clip, ANIM_SCALE.transition[charIndexRef.current] ?? 0.5);
        transitionActionsRef.current.t1 = activeMixer.clipAction(t1Clip);
        transitionActionsRef.current.t2 = activeMixer.clipAction(t2Clip);
        console.log(`[AnimationManager] ✅ Transition ready for char ${charIndexRef.current}`);
      }

    }).catch((err) => {
      console.warn('[AnimationManager] Load error:', err);
    });

    // ✅ FIX: rAF DOMHighResTimeStamp — sub-ms precision, no delta spikes
    let animFrameId;
    let lastTime = null;
    const animate = (timestamp) => {
      if (lastTime === null) lastTime = timestamp;
      const delta = (timestamp - lastTime) / 1000;
      lastTime = timestamp;
      mixersRef.current.forEach(mixer => mixer?.update(delta));
      animFrameId = requestAnimationFrame(animate);
    };
    animFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animFrameId);
      mixersRef.current.forEach(mixer => mixer?.stopAllAction());
      mixersRef.current = [];
      idleActionsRef.current = [];
      transitionActionsRef.current = { t1: null, t2: null };
    };
  }, []); // ✅ Once — never rebuilds

  // ─────────────────────────────────────────────
  // 2. charIndex changes — rebuild transition actions only
  //    Idle loops UNTOUCHED — already running on all 4 mixers
  // ─────────────────────────────────────────────
  useEffect(() => {
    const mixers = mixersRef.current;
    if (mixers.length === 0) return;

    const activeMixer = mixers[charIndex - 1];
    if (!activeMixer) return;

    transitionActionsRef.current.t1?.stop();
    transitionActionsRef.current.t2?.stop();

    const loader = new FBXLoader();
    Promise.all([
      new Promise((resolve, reject) => loader.load('/assets/models/animation/t1-combined.fbx', (fbx) => {
        const clip = fbx.animations[0]; if (!clip) { reject(); return; }
        clip.name = 't1-combined'; resolve(clip);
      }, undefined, reject)),
      new Promise((resolve, reject) => loader.load('/assets/models/animation/t2-combined.fbx', (fbx) => {
        const clip = fbx.animations[0]; if (!clip) { reject(); return; }
        clip.name = 't2-combined'; resolve(clip);
      }, undefined, reject)),
    ]).then(([t1Raw, t2Raw]) => {
      const t1Clip = THREE.AnimationClip.parse(THREE.AnimationClip.toJSON(t1Raw));
      const t2Clip = THREE.AnimationClip.parse(THREE.AnimationClip.toJSON(t2Raw));
      t1Clip.name = 't1-combined';
      t2Clip.name = 't2-combined';
      applyScaleToClip(t1Clip, ANIM_SCALE.transition[charIndex] ?? 0.5);
      applyScaleToClip(t2Clip, ANIM_SCALE.transition[charIndex] ?? 0.5);
      transitionActionsRef.current.t1 = activeMixer.clipAction(t1Clip);
      transitionActionsRef.current.t2 = activeMixer.clipAction(t2Clip);
      console.log(`[AnimationManager] ✅ Transition rebuilt for char ${charIndex}`);
    });
  }, [charIndex]);

  // ─────────────────────────────────────────────
  // 3. Phase changes — transitions overlay on active char
  //    Idles keep looping underneath always
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (phase === 'SELECTION' || phase === 'EXERCISE') {
      const t1 = transitionActionsRef.current.t1;
      const t2 = transitionActionsRef.current.t2;
      const idleAction = idleActionsRef.current[charIndexRef.current - 1];

      t1?.stop();
      t2?.stop();

      // ✅ FIX: guard reset — don't interrupt a clean mid-loop cycle
      if (idleAction && !idleAction.isRunning()) {
        idleAction.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.1).play();
        console.log(`[AnimationManager] ✅ ${phase}: idle restarted with fadeIn`);
      } else if (idleAction) {
        idleAction.fadeIn(0.1); // already running, just restore weight
        console.log(`[AnimationManager] ✅ ${phase}: idle already running, fadeIn weight only`);
      }

    } else if (phase === 'TRANSITION1') {
      const action = transitionActionsRef.current.t1;
      if (action) {
        action.reset().setLoop(THREE.LoopOnce, 1).fadeIn(0.2).play();
        action.clampWhenFinished = true;
        console.log('[AnimationManager] ✅ TRANSITION1 playing');
      }

    } else if (phase === 'TRANSITION2') {
      const action = transitionActionsRef.current.t2;
      if (action) {
        action.reset().setLoop(THREE.LoopOnce, 1).fadeIn(0.2).play();
        action.clampWhenFinished = true;
        console.log('[AnimationManager] ✅ TRANSITION2 playing');
      }
    }
  }, [phase]);

  return null;
}